import crypto from 'crypto-es';
import { useEffect, useRef, useState } from 'react';
import LoadingError from './LoadingError/loadingError';
import * as S from './partnerOffers.style';
import { PartnerOffersProps, PlaceHolder } from './partnerOffers.models';
import { validationSchema } from './partnerOffers.validation';
import { Button } from '@vfit/shared/atoms';
import { Form, VfModal } from '@vfit/shared/components';
import { yupResolver } from '@hookform/resolvers/yup';
import { useFormExtended } from '@vfit/consumer/hooks';
import {
  checkIsApp,
  PARTNER_OFFER_COOKIE,
  usePartnershipVMSValidate,
} from '@vfit/consumer/data-access';
import { purify } from '@vfit/shared/themes';
import {
  getButtonActionByActionType,
  getVariantByActionStyle,
  setItemCookie,
  trackLink,
  trackView,
} from '@vfit/shared/data-access';
import Summary from './Summary/summary';
import { IActionType } from '@vfit/shared/models';
import { ExitIcon } from '@vfit/shared-icons';

const PartnerOffers = ({ partnerOffersData }: PartnerOffersProps) => {
  const { placeHolder, data, cmsValidationConfig, loadingErrorConfig, summaryConfig } =
    partnerOffersData || {};
  const { SHA256 } = crypto;
  const {
    mutate: voucherMutate,
    isSuccess,
    isLoading,
    isError,
    error,
    reset: resetMutation,
  } = usePartnershipVMSValidate();

  const [disclaimer] = purify([data?.disclaimer]);

  const {
    hookReturn: {
      register,
      formState: { errors },
      getValues,
      resetField,
      watch,
    },
    isAllRequiredValid,
  } = useFormExtended<PlaceHolder>({
    resolver: yupResolver(validationSchema(cmsValidationConfig)),
    mode: 'all',
    defaultValues: {
      firstName: '',
      lastName: '',
      codeFiscal: '',
      codePromo: '',
    },
  });
  const [modal, setModal] = useState({
    show: false,
  });
  const [SummaryModal, setSummaryModal] = useState({
    show: false,
  });
  const firstName = watch('firstName');
  const lastName = watch('lastName');
  const codeFiscal = watch('codeFiscal');
  const codePromo = watch('codePromo');

  let currentDomain = 'privati.vodafone.it';
  let prefix;

  const handleSubmit = async () => {
    const inputs = getValues();
    const payload = {
      campaignId: data?.campaignId ? +data?.campaignId : 0,
      voucherId: inputs.codePromo || '',
      encryptCF: SHA256(inputs.codeFiscal)?.toString() || '',
    };
    if (data?.action?.type?.toString() === IActionType.PARTNERSHIP_OFFER_ACTION?.toString()) {
      if (window) {
        currentDomain = window?.location?.hostname;
      }
      await setItemCookie(
        PARTNER_OFFER_COOKIE,
        {
          firstName: inputs?.firstName,
          lastName: inputs?.lastName,
          fiscalCode: inputs?.codeFiscal,
        },
        0.021,
        false,
        currentDomain
      );
      voucherMutate(payload);
    } else {
      getButtonActionByActionType(data?.action);
    }
  };

  const handleCloseModal = () => {
    setModal({ show: false });
    setSummaryModal({ show: false });
    trackLink('X');
    resetMutation();
  };

  const showSummaryModal = () => {
    trackLink('attiva ora');
    setSummaryModal({ show: true });
  };

  const ref = useRef(null);
  const hasFiredRef = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasFiredRef.current) {
          hasFiredRef.current = true;
          trackView({
            event_label_track: 'form start',
            event_name: 'view',
            journey_name: window['utag_data']?.journey_name || 'dynamic form',
            journey_type: window['utag_data']?.journey_type || 'journey',
            page_section: window['utag_data']?.page_section || 'dynamic form',
            page_subsection: window['utag_data']?.page_subsection || undefined,
          });
          observer.disconnect();
        }
      },
      {
        threshold: 0.3,
      }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    if (!isError) return;
    setSummaryModal({ show: false });
    setModal({ show: true });
  }, [isError]);

  trackView({
    event_name: ['NEW WIDGET PARTNER OFFER'],
    journey_name: 'dynamic form',
  });

  useEffect(() => {
    if (window) {
      prefix = window?.location?.origin;
    }
    const url = new URL(`${prefix}/${data?.url}`);
    url.searchParams.append('sk-old-po', 'true');
    if (isSuccess) {
      window.location.href = url?.toString();
    }
  }, [isSuccess]);

  const [pTitle, pDescription] = purify([data?.title, data?.description || '']);
  return (
    <S.Wrapper ref={ref}>
      <S.Card>
        <S.Container>
          {pTitle && <S.Title dangerouslySetInnerHTML={{ __html: pTitle }} />}
          <S.InputContainer>
            <S.InputRow>
              <div className="textInput">
                <Form.TextInput
                  data-cs-mask=""
                  label={placeHolder?.firstName || ''}
                  placeholder={placeHolder?.firstName || ''}
                  error={errors.firstName?.message}
                  {...register('firstName')}
                  onIconClick={() => resetField('firstName')}
                  icon={firstName ? <ExitIcon /> : undefined}
                />
              </div>
              <div className="textInput">
                <Form.TextInput
                  data-cs-mask=""
                  label={placeHolder?.lastName || ''}
                  placeholder={placeHolder?.lastName || ''}
                  error={errors.lastName?.message}
                  {...register('lastName')}
                  onIconClick={() => resetField('lastName')}
                  icon={lastName ? <ExitIcon /> : undefined}
                />
              </div>
              <div className="textInput">
                <Form.TextInput
                  data-cs-mask=""
                  label={placeHolder?.codeFiscal || ''}
                  placeholder={placeHolder?.codeFiscal || ''}
                  error={errors.codeFiscal?.message}
                  {...register('codeFiscal')}
                  onIconClick={() => resetField('codeFiscal')}
                  icon={codeFiscal ? <ExitIcon /> : undefined}
                />
              </div>
              <div className="textInput">
                <Form.TextInput
                  data-cs-mask=""
                  label={placeHolder?.codePromo || ''}
                  placeholder={placeHolder?.codePromo || ''}
                  error={errors.codePromo?.message}
                  {...register('codePromo')}
                  onIconClick={() => resetField('codePromo')}
                  icon={codePromo ? <ExitIcon /> : undefined}
                />
              </div>
            </S.InputRow>
            <S.Description>
              {pDescription && <S.Text dangerouslySetInnerHTML={{ __html: pDescription }} />}
            </S.Description>
          </S.InputContainer>
          <S.ButtonContainer>
            <div className="buttonSlide">
              <Button
                disabled={!isAllRequiredValid || isLoading}
                onClick={showSummaryModal}
                variant={
                  checkIsApp() ? 'mva:primary' : getVariantByActionStyle(data?.action?.style)
                }
                name="action_retrieveFiscalCode"
                aria-label={data?.action?.title}
              >
                {data?.action?.title}
              </Button>
            </div>
          </S.ButtonContainer>
        </S.Container>
      </S.Card>
      <S.Disclamer>
        {disclaimer && <S.Text dangerouslySetInnerHTML={{ __html: disclaimer }} />}
      </S.Disclamer>
      {loadingErrorConfig && setModal && (
        <VfModal height={377} isOpen={modal.show} handleClose={handleCloseModal}>
          <>
            <LoadingError
              loadingErrorConfig={loadingErrorConfig}
              setModal={setModal}
              error={error}
            />
          </>
        </VfModal>
      )}
      <VfModal height={518} isOpen={SummaryModal.show} handleClose={handleCloseModal}>
        <>
          <Summary
            summaryConfig={summaryConfig}
            setModal={setSummaryModal}
            handleSubmit={handleSubmit}
            isLoading={isLoading}
            codeFiscal={codeFiscal}
          />
        </>
      </VfModal>
    </S.Wrapper>
  );
};
export default PartnerOffers;

